/**
 * Share State Module
 *
 * Tracks the current active share and role information.
 * This is a separate module to avoid circular dependencies between
 * interfaces/classic/store/index.js and crdt-bridge.js.
 */

import { isDocQuotaBlocked } from "../services/quota-state.js";
import { isMapFull } from "../services/map-size.js";

// Currently active share ID (null when viewing main map)
let activeShareId = null;

// Map of shareId -> role (viewer/editor/admin/owner)
const sharedRoles = new Map();

// Resolves the id of the document open as the main map. Registered by
// crdt-bridge. With document-level sharing a shared map normally opens as the
// main document (not through the share-view path that sets activeShareId), so
// its role has to gate edits too — otherwise a viewer's edits apply locally,
// are refused by the server and wedge the sync (unacknowledged deltas).
let mainDocIdResolver = null;

/**
 * @param {(() => string|null)|null} resolver
 */
export function setMainDocIdResolver(resolver) {
  mainDocIdResolver = resolver;
}

/**
 * Set the currently active shared doc
 * @param {string|null} shareId
 */
export function setActiveShareId(shareId) {
  activeShareId = shareId;
}

/**
 * Get the currently active shared doc ID
 * @returns {string|null}
 */
export function getActiveShareId() {
  return activeShareId;
}

/**
 * Set the role for a share
 * @param {string} shareId
 * @param {string} role - 'viewer', 'editor', 'admin', or 'owner'
 */
export function setShareRole(shareId, role) {
  sharedRoles.set(shareId, role);
}

/**
 * Get the role for a share
 * @param {string} shareId
 * @returns {string|null}
 */
export function getShareRole(shareId) {
  return sharedRoles.get(shareId) || null;
}

/**
 * Remove a share's role (on cleanup)
 * @param {string} shareId
 */
export function removeShareRole(shareId) {
  sharedRoles.delete(shareId);
}

/**
 * Get the role for the active share
 * @returns {string|null} - 'viewer', 'editor', 'admin', 'owner', or null if no active share
 */
export function getActiveShareRole() {
  if (!activeShareId) return null;
  return sharedRoles.get(activeShareId) || null;
}

/**
 * Check if the open map is read-only: the user is a viewer, the storage
 * quota is reached, or the map is full (engine/services/map-size.js).
 * @returns {boolean}
 */
export function isActiveShareReadOnly() {
  return isActiveShareDeleteBlocked() || isMapFull(activeShareId || mainDocIdResolver?.() || null);
}

/**
 * Read-only in a way that also stops deleting: viewer or storage quota. A
 * full map still allows deleting and cutting, so the user can make room.
 * @returns {boolean}
 */
export function isActiveShareDeleteBlocked() {
  if (isDocQuotaBlocked()) return true;
  const docId = activeShareId || mainDocIdResolver?.() || null;
  if (!docId) return false;
  const role = sharedRoles.get(docId);
  return role === "viewer";
}

/** Why an edit of the open map is refused, for the message. */
export function activeReadOnlyReason() {
  return isActiveShareDeleteBlocked() ? "readOnly" : "mapFull";
}
